import { gql } from '@apollo/client';

import { INTEGRATION_EVENT_HOOK_FIELDS } from '@/graphql/fragments/integration_event_hooks';
import { PRICING_OVERRIDE_FIELDS } from '@/graphql/fragments/pricing_overrides';
import { CORE_CLIENT_ACCOUNT_FIELDS } from '@/graphql/fragments/clients';
import { PRICING_TARIFF_FIELDS } from '@/graphql/fragments/pricing_tariffs';
import { DOCUMENT_FIELDS } from '@/graphql/queries/documents';

// The Carriers grid builds one row per carrier from overrides and partnerships alike.
const CORP_CARRIER_FIELDS = gql`
    ${CORE_CLIENT_ACCOUNT_FIELDS}

    fragment CorpCarrierFields on clients {
        ...CoreClientAccountFields
        dot_number
        locations {
            location_id
            business_zip
        }
        user {
            user_id
            roles
        }
    }
`;

// Shipper rows, defaults and inserted rows share this selection, so the cached rows stay complete.
const CORP_INTEGRATION_OVERRIDE_FIELDS = gql`
    ${INTEGRATION_EVENT_HOOK_FIELDS}
    ${CORP_CARRIER_FIELDS}

    fragment CorpIntegrationOverrideFields on integration_overrides {
        override_id
        carrier_id
        client_id
        shipper_id
        middle_mile_carrier_id
        integration_meta_type_id
        organization_id
        is_shipper_default
        type
        config
        default_order
        rec_isa_id
        send_isa_id
        status
        last_success_at
        subscriptions {
            ...IntegrationEventHookFields
        }
        poll {
            poll_id
        }
        carrier {
            ...CorpCarrierFields
        }
        middle_mile_carrier {
            ...CorpCarrierFields
        }
    }
`;

export const GET_CORP_INTEGRATION_OVERRIDES = gql`
    ${CORP_INTEGRATION_OVERRIDE_FIELDS}

    query GetCorpIntegrationOverrides($orgShipperIds: [uuid!]!) {
        integration_overrides(where: { shipper_id: { _in: $orgShipperIds } }) {
            ...CorpIntegrationOverrideFields
        }
    }
`;

export const GET_CORP_SHIPPER_PARTNERSHIPS = gql`
    ${CORP_CARRIER_FIELDS}

    query GetCorpShipperPartnerships($orgShipperIds: [uuid!]!) {
        partnerships(where: { shipper_id: { _in: $orgShipperIds } }) {
            partnership_id
            shipper_id
            carrier_id
            carrier {
                ...CorpCarrierFields
            }
        }
    }
`;

export const GET_CORP_DEFAULT_INTEGRATIONS = gql`
    ${CORP_INTEGRATION_OVERRIDE_FIELDS}

    query GetCorpDefaultIntegrations($organization_id: uuid!) {
        integration_overrides(where: { organization_id: { _eq: $organization_id } }) {
            ...CorpIntegrationOverrideFields
        }
    }
`;

// A shipper's modal lists the default beside the shipper's own rows, so a change to either refetches both.
export const CORP_INTEGRATION_QUERIES = ['GetCorpIntegrationOverrides', 'GetCorpDefaultIntegrations'];

export const GET_CORP_RESERVATION_OVERRIDES = gql`
    query GetCorpReservationOverrides($orgShipperIds: [uuid!]!) {
        reservation_overrides(where: { shipper_id: { _in: $orgShipperIds } }) {
            reservation_override_id
            shipper_id
            carrier_id
            middle_mile_carrier_id
            reservation_type
        }
    }
`;

export const GET_CORP_PRICING_OVERRIDES = gql`
    ${PRICING_OVERRIDE_FIELDS}
    ${CORE_CLIENT_ACCOUNT_FIELDS}

    query GetCorpPricingOverrides($orgShipperIds: [uuid!]!, $organization_id: uuid!) {
        pricing_overrides(
            where: {
                _and: [
                    { oms: { _eq: false } }
                    {
                        _or: [
                            { client_id: { _in: $orgShipperIds } }
                            { partner_client_id: { _in: $orgShipperIds } }
                            { organization_id: { _eq: $organization_id } }
                            # A contractor's pay is set against the org's Flex Center. Shipper-specific contractor
                            # rates have no editor yet, so only the all-shippers rate is shown and edited here.
                            {
                                override_type: { _eq: "CONTRACTOR" }
                                shipper_id: { _is_null: true }
                                partner_client: { user: { organization_id: { _eq: $organization_id } } }
                            }
                        ]
                    }
                ]
            }
        ) {
            ...PricingOverrideFields
            organization_id
            client {
                ...CoreClientAccountFields
                dot_number
                locations {
                    location_id
                    business_zip
                }
                user {
                    user_id
                    roles
                }
            }
            partner_client {
                ...CoreClientAccountFields
                dot_number
                locations {
                    location_id
                    business_zip
                }
                user {
                    user_id
                    roles
                }
            }
            assign {
                ...CoreClientAccountFields
                dot_number
                locations {
                    location_id
                    business_zip
                }
                user {
                    user_id
                    roles
                }
            }
        }
    }
`;

/** A carrier's rate for every shipper, outside any org. Pricing falls back to it after the shipper's own rate and the org default. */
export const GET_CORP_CARRIER_STANDARD_PRICING = gql`
    query GetCorpCarrierStandardPricing($carrierIds: [uuid!]!) {
        pricing_overrides(
            where: {
                client_id: { _in: $carrierIds }
                partner_client_id: { _is_null: true }
                organization_id: { _is_null: true }
                oms: { _eq: false }
                enabled: { _eq: true }
                override_type: { _in: ["E2E", "FINAL_MILE", "GIG"] }
            }
        ) {
            pricing_override_id
            client_id
            enabled
            override_type
            algo_type
            tariff_id
            tariff {
                tariff_id
                name
            }
        }
    }
`;

export const INSERT_CORP_INTEGRATION_OVERRIDE = gql`
    ${CORP_INTEGRATION_OVERRIDE_FIELDS}

    mutation InsertCorpIntegrationOverride(
        $object: integration_overrides_insert_input!
        $partnerships: [partnerships_insert_input!]! = []
    ) {
        insert_integration_overrides_one(object: $object) {
            ...CorpIntegrationOverrideFields
        }
        partnerships: insert_partnerships(
            objects: $partnerships
            on_conflict: { constraint: partnerships_shipper_id_carrier_id_key, update_columns: [] }
        ) {
            affected_rows
        }
    }
`;

export const DELETE_CORP_INTEGRATION_OVERRIDE = gql`
    mutation DeleteCorpIntegrationOverride($override_id: uuid!, $hook_ids: [uuid!]!) {
        deleted_hooks: delete_integration_event_hooks(where: { event_subscription_id: { _in: $hook_ids } }) {
            affected_rows
        }
        deleted_override: delete_integration_overrides_by_pk(override_id: $override_id) {
            override_id
        }
    }
`;

export const UPDATE_CORP_INTEGRATION_OVERRIDE = gql`
    mutation UpdateCorpIntegrationOverride($override_id: uuid!, $config: jsonb!) {
        updated: update_integration_overrides_by_pk(
            pk_columns: { override_id: $override_id }
            _set: { config: $config }
        ) {
            override_id
            config
        }
    }
`;

export const DISABLE_SHIPPER_RATE_SHOP_OVERRIDES = gql`
    mutation DisableShipperRateShopOverrides($client_id: uuid!) {
        update_pricing_overrides(
            where: { client_id: { _eq: $client_id }, assign_id: { _is_null: false } }
            _set: { enabled: false }
        ) {
            affected_rows
            returning {
                pricing_override_id
                enabled
            }
        }
    }
`;

export const INSERT_CORP_PRICING_OVERRIDE = gql`
    ${PRICING_OVERRIDE_FIELDS}
    ${CORE_CLIENT_ACCOUNT_FIELDS}

    mutation InsertCorpPricingOverride(
        $object: pricing_overrides_insert_input!
        $partnerships: [partnerships_insert_input!]! = []
    ) {
        inserted: insert_pricing_overrides_one(object: $object) {
            ...PricingOverrideFields
            organization_id
            client {
                ...CoreClientAccountFields
                dot_number
                locations {
                    location_id
                    business_zip
                }
                user {
                    user_id
                    roles
                }
            }
            partner_client {
                ...CoreClientAccountFields
                dot_number
                locations {
                    location_id
                    business_zip
                }
                user {
                    user_id
                    roles
                }
            }
            assign {
                ...CoreClientAccountFields
                dot_number
                locations {
                    location_id
                    business_zip
                }
                user {
                    user_id
                    roles
                }
            }
        }
        partnerships: insert_partnerships(
            objects: $partnerships
            on_conflict: { constraint: partnerships_shipper_id_carrier_id_key, update_columns: [] }
        ) {
            affected_rows
        }
    }
`;

export const UPDATE_CORP_PRICING_OVERRIDE = gql`
    ${PRICING_OVERRIDE_FIELDS}
    ${CORE_CLIENT_ACCOUNT_FIELDS}

    mutation UpdateCorpPricingOverride($pricing_override_id: uuid!, $patch: pricing_overrides_set_input!) {
        updated: update_pricing_overrides_by_pk(
            pk_columns: { pricing_override_id: $pricing_override_id }
            _set: $patch
        ) {
            ...PricingOverrideFields
            partner_client {
                ...CoreClientAccountFields
                dot_number
                locations {
                    location_id
                    business_zip
                }
                user {
                    user_id
                    roles
                }
            }
            assign {
                ...CoreClientAccountFields
                dot_number
                locations {
                    location_id
                    business_zip
                }
                user {
                    user_id
                    roles
                }
            }
        }
    }
`;

export const GET_CORP_DOCUMENTS = gql`
    ${DOCUMENT_FIELDS}

    query GetCorpDocuments($orgCarrierIds: [uuid!]!, $orgShipperIds: [uuid!]!, $organization_id: uuid!) {
        org_documents: documents(
            where: {
                _and: [
                    { archived: { _eq: false } }
                    { type: { _in: ["AGREEMENT", "COI", "W9"] } }
                    { client_id: { _in: $orgCarrierIds } }
                    { organization_id: { _eq: $organization_id } }
                ]
            }
        ) {
            ...DocumentFields
        }
        rate_addendums: documents(
            where: {
                _and: [
                    { archived: { _eq: false } }
                    { type: { _eq: "RATES" } }
                    { partner_client_id: { _in: $orgShipperIds } }
                    { organization_id: { _eq: $organization_id } }
                ]
            }
        ) {
            ...DocumentFields
        }
    }
`;

/**
 * Archive an org document with nothing replacing it. No compliance review is recorded: onward-hasura
 * `documents_compliance_rereview` skips archived rows.
 */
export const ARCHIVE_CORP_DOCUMENT = gql`
    mutation ArchiveCorpDocument($document_id: uuid!) {
        update_documents_by_pk(pk_columns: { document_id: $document_id }, _set: { archived: true }) {
            document_id
            archived
        }
    }
`;

/**
 * Upload an org document in one transaction: archive the one it replaces (`archive_ids`, empty for a first upload) and
 * insert the new one. A new AGREEMENT, COI or W9 for an approved contractor sends it back for review in the database
 * (onward-hasura `documents_compliance_rereview`), which records a PENDING compliance review.
 */
export const REPLACE_CORP_DOCUMENT = gql`
    ${DOCUMENT_FIELDS}

    mutation ReplaceCorpDocument($archive_ids: [uuid!]!, $object: documents_insert_input!) {
        archived: update_documents(where: { document_id: { _in: $archive_ids } }, _set: { archived: true }) {
            returning {
                document_id
                archived
            }
        }
        document: insert_documents_one(object: $object) {
            ...DocumentFields
        }
    }
`;

export const GET_CORP_TARIFFS = gql`
    ${PRICING_TARIFF_FIELDS}

    query GetCorpTariffs($client_ids: [uuid!]!, $organization_id: uuid!) {
        tariffs: pricing_tariffs(
            where: {
                _and: [
                    { _or: [{ client_id: { _in: $client_ids } }, { organization_id: { _eq: $organization_id } }] }
                    { tariff_type: { _eq: "ORDER" } }
                ]
            }
            order_by: { created_at: desc }
        ) {
            ...PricingTariffFields
        }
    }
`;

const EXTERNAL_IDENTIFIER_FIELDS = gql`
    fragment ExternalIdentifierFields on external_identifiers {
        id
        key
        label
        value
        client_id
        organization_id
    }
`;

// The org's editable keys. The frontend renders one field per definition — keys are never
// hardcoded, and are not filtered by the client's account type.
export const GET_EXTERNAL_IDENTIFIER_DEFINITIONS = gql`
    query GetExternalIdentifierDefinitions($organization_id: uuid!) {
        external_identifier_definitions(
            where: { organization_id: { _eq: $organization_id } }
            order_by: { label: asc }
        ) {
            id
            key
            label
            description
        }
    }
`;

export const GET_EXTERNAL_IDENTIFIERS = gql`
    ${EXTERNAL_IDENTIFIER_FIELDS}

    query GetExternalIdentifiers($organization_id: uuid!, $client_id: uuid!) {
        external_identifiers(where: { organization_id: { _eq: $organization_id }, client_id: { _eq: $client_id } }) {
            ...ExternalIdentifierFields
        }
    }
`;

export const UPSERT_EXTERNAL_IDENTIFIER = gql`
    ${EXTERNAL_IDENTIFIER_FIELDS}

    mutation UpsertExternalIdentifier($object: external_identifiers_insert_input!) {
        insert_external_identifiers_one(
            object: $object
            on_conflict: {
                constraint: external_identifiers_organization_id_client_id_key_key
                update_columns: [value, label]
            }
        ) {
            ...ExternalIdentifierFields
        }
    }
`;

export const DELETE_EXTERNAL_IDENTIFIER = gql`
    mutation DeleteExternalIdentifier($id: uuid!) {
        delete_external_identifiers_by_pk(id: $id) {
            id
        }
    }
`;

export const GET_ORG_PROFILE_ASSIGNMENTS = gql`
    query GetOrgProfileAssignments($organization_id: uuid!) {
        delivery_profiles(where: { organization_id: { _eq: $organization_id } }) {
            delivery_profile_id
            name
            auto_assignments {
                shipper_id
            }
            subregions {
                subregion_id
                carriers {
                    mapping_id
                    carrier_id
                    middle_mile_carrier_id
                    warehouse_carrier_id
                }
            }
        }
    }
`;

// Not sourced from admin_clients on purpose: org membership alone doesn't say which flex centers
// hand a contractor work, and that linkage is the point of the tab.
export const GET_ORG_CONTRACTOR_PARTNERSHIPS = gql`
    ${CORE_CLIENT_ACCOUNT_FIELDS}

    query GetOrgContractorPartnerships($orgFlexCenterIds: [uuid!]!) {
        partnerships(
            where: { _and: [{ carrier_id: { _in: $orgFlexCenterIds } }, { contractor_id: { _is_null: false } }] }
        ) {
            partnership_id
            status
            carrier_id
            contractor_id
            carrier {
                client_id
                business_name
            }
            contractor {
                ...CoreClientAccountFields
                locations {
                    location_id
                    business_zip
                }
                user {
                    user_id
                    roles
                }
            }
        }
    }
`;

// No on_conflict: partnerships_pairing_unique_idx is an expression index, which Hasura can't target.
export const INSERT_CONTRACTOR_PARTNERSHIPS_WITH_PRICING = gql`
    mutation InsertContractorPartnershipsWithPricing(
        $objects: [partnerships_insert_input!]!
        $pricing: [pricing_overrides_insert_input!]! = []
        $repricedIds: [uuid!]! = []
        $repricing: pricing_overrides_set_input! = {}
    ) {
        insert_partnerships(objects: $objects) {
            affected_rows
        }
        insert_pricing_overrides(objects: $pricing) {
            affected_rows
        }
        update_pricing_overrides(where: { pricing_override_id: { _in: $repricedIds } }, _set: $repricing) {
            affected_rows
        }
    }
`;
